import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

const getContactCreditNotes = (ID) => {
    return Axios.get(`/contacts/creditnotes/${ID}`);
};

const useCreditNotes = (ID) => {
    return useQuery({
        queryKey: ['creditnotes-contact', ID],
        queryFn: () => getContactCreditNotes(ID),
        enabled: true,
        refetchOnWindowFocus: false,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const forceReloadCreditNotes = (ID) => queryClient.invalidateQueries({ queryKey: ['creditnotes-contact', ID] });

const getCreditNote = (ID) => {
    return Axios.get(`/CreditNotes/${ID}`);
};

const useCreditNote = (ID) => {
    return useQuery({
        queryKey: ['creditnotes', ID],
        queryFn: () => getCreditNote(ID),
        staleTime: 10000,
        cacheTime: 10000,
        retry: false,
        enabled: ID ? true : false,
    });
};

const forceReloadCreditNote = (ID) => queryClient.invalidateQueries({ queryKey: ['creditnotes', ID] });

const onAddNewCreditNote = ({ data }) => {
    return Axios.post(`/CreditNotes`, data);
};

const useAddNewCreditNote = () => {
    return useMutation({
        mutationFn: onAddNewCreditNote,
        onSuccess: () => {},
    });
};

const onUpdateCreditNote = ({ data }) => {
    return Axios.put(`/CreditNotes`, data);
};

const useUpdateCreditNote = () => {
    return useMutation({
        mutationFn: onUpdateCreditNote,
        onSuccess: (_, variables) => {
            forceReloadCreditNote(variables.data.CreditNoteID);
        },
    });
};

const onSortRows = ({ ID, data }) => {
    return Axios.put(`/CreditNotes/rows/SetOrder/${ID}`, data);
};

const useSortRows = () => {
    return useMutation({
        mutationFn: onSortRows,
        onSuccess: (_, variables) => {
            forceReloadCreditNote(variables.ID);
        },
    });
};

const onDeleteRow = ({ data, type }) => {
    if (type === 'Statement') {
        return Axios.delete('/CreditNotes/rows/statement', { data });
    }
    return Axios.delete('/CreditNotes/rows/text', { data });
};

const useDeleteRow = () => {
    return useMutation({
        mutationFn: onDeleteRow,
        onSuccess: (_, variables) => {
            forceReloadCreditNote(variables.data.CreditNoteID);
        },
    });
};

const onDeleteCreditNote = (ID) => {
    return Axios.delete(`/CreditNotes/${ID}`);
};

const useDeleteCreditNote = () => {
    return useMutation({
        mutationFn: onDeleteCreditNote,
        onSuccess: () => {},
    });
};

const onUpdatePaymentStatus = ({ data }) => {
    return Axios.put('/CreditNotes/SetPaymentStatus', data);
};

const useUpdatePaymentStatus = () => {
    return useMutation({
        mutationFn: onUpdatePaymentStatus,
        onSuccess: (_, variables) => {
            forceReloadCreditNote(variables.data.CreditNoteID);
        },
    });
};

const onCreditNoteConfirm = ({ data }) => {
    return Axios.put('/CreditNotes/Confirm', data);
};

const useCreditNoteConfirm = () => {
    return useMutation({
        mutationFn: onCreditNoteConfirm,
        onSuccess: (_, variables) => {
            forceReloadCreditNote(variables.data.CreditNoteID);
        },
    });
};

const onClearCreditNoteConfirm = (ID) => {
    return Axios.put(`/CreditNotes/Unconfirm/${ID}`);
};

const useClearCreditNoteConfirm = () => {
    return useMutation({
        mutationFn: onClearCreditNoteConfirm,
        onSuccess: (_, ID) => {
            forceReloadCreditNote(ID);
        },
    });
};

export {
    forceReloadCreditNote,
    forceReloadCreditNotes,
    useAddNewCreditNote,
    useClearCreditNoteConfirm,
    useDeleteCreditNote,
    useDeleteRow,
    useCreditNote,
    useCreditNoteConfirm,
    useCreditNotes,
    useSortRows,
    useUpdateCreditNote,
    useUpdatePaymentStatus,
};
